import { useEffect, useRef, useState } from 'react';
import { useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { Loader2, RefreshCw, WifiOff } from 'lucide-react';
import type { NivelAviso } from '../../hooks/avisoConexion';
import styles from './ReconnectionOverlay.module.css';

export interface ReconnectionOverlayProps {
  /** Override the auto-detected state (useful for testing) */
  state?: ConnectionState;
  /** Optional: show how many seconds we have been trying */
  showElapsed?: boolean;
  /**
   * Explicación concreta de por qué no conecta, cuando el backend la conoce
   * (servidor de video sin responder, fuera del horario de servicio). Sustituye
   * al texto genérico: quien lleva un minuto esperando necesita saber la causa,
   * no que "está tardando más de lo normal".
   */
  motivo?: string | null;
  /**
   * Called when the user presses "Reintentar conexión". Should refresh the
   * LiveKit token and force a reconnect from the same screen (sin salir de
   * la pantalla de atención). When provided, a retry button is shown while
   * the connection is `Disconnected`.
   */
  onRetry?: () => void;
  /**
   * Peldaño del aviso de conexión (`useAvisoConexion`). Cuando se pasa, una interrupción EN
   * MEDIO de la llamada solo abre este overlay al llegar a `grave` (>25 s): antes de eso la
   * propuesta de avisos pide silencio los primeros 8 s y un banner discreto entre 8 y 25 s.
   * El `Connecting` estancado de la conexión INICIAL no pasa por aquí y se sigue mostrando
   * con su propio umbral: ahí no hay llamada que interrumpir, no hay nada que preservar.
   *
   * Se toma el tipo de `avisoConexion` en vez de repetir la unión a mano: al agregarse el
   * peldaño 'soloAudio' esta copia quedó desfasada y el build falló. `soloAudio` NO abre el
   * overlay a propósito — la llamada sigue por audio y no hay nada que interrumpir.
   */
  nivelAviso?: NivelAviso;
}

/**
 * Full-card overlay shown when LiveKit's `ConnectionState` transitions to
 * `Reconnecting` or `Disconnected`. Disappears once the connection is
 * `Connected` again.
 *
 * Must be rendered INSIDE a `<LiveKitRoom>` for the auto-detected state
 * to work; otherwise pass `state` explicitly.
 */
/** Segundos de problemas antes de mostrar el botón de reintento manual (se le
 *  da margen al auto-reconnect de LiveKit). Una vez mostrado, el botón
 *  permanece FIJO hasta que la conexión se restablece. */
const RETRY_BUTTON_DELAY_S = 3;
/** Segundos en `Connecting` (conexión INICIAL) antes de considerar que está
 *  estancado y mostrar el overlay con botón de reintento. Más largo que el de
 *  reconexión: un connect inicial legítimo sobre TURN/relay tarda unos segundos
 *  y no queremos alarmar al médico en un arranque normal. Esto cubre el caso
 *  "Conectando… y no avanza" cuando el servidor self-hosted se está reiniciando
 *  por mantenimiento justo al entrar a la llamada. */
const CONNECTING_STUCK_DELAY_S = 8;
/** Debounce (ms) antes de ocultar el overlay cuando vuelve "connected", para
 *  evitar parpadeo durante intermitencia (connected↔reconnecting↔disconnected). */
const HIDE_DEBOUNCE_MS = 1500;

export function ReconnectionOverlay({ state, showElapsed = true, onRetry, motivo, nivelAviso }: ReconnectionOverlayProps) {
  const autoState = useConnectionState();
  const current = state ?? autoState;
  const [elapsed, setElapsed] = useState(0);
  // Segundos continuos en `Connecting` (conexión inicial) para distinguir un
  // arranque normal de uno estancado.
  const [connectingSecs, setConnectingSecs] = useState(0);
  // Visibilidad "pegajosa": aparece al primer signo de problema y solo se
  // oculta cuando la conexión se mantiene estable (debounce). Así el overlay y
  // el botón no parpadean mientras la conexión va y viene.
  const [troubled, setTroubled] = useState(false);
  /* ¿Llegó a conectarse alguna vez? Distingue «no logro entrar» de «se cortó a mitad», que
     merecen respuestas distintas. Va en una ref y no en estado porque no necesita provocar un
     render: cuando el valor importa —al pasar a reconectando— el cambio de `current` ya
     dispara uno. */
  const yaConectoRef = useRef(false);

  const isReconnecting = current === ConnectionState.Reconnecting;
  const isDisconnected = current === ConnectionState.Disconnected;
  const isConnecting = current === ConnectionState.Connecting;
  const isConnected = current === ConnectionState.Connected;
  // Un `Connecting` que se prolonga cuenta como problema (connect inicial
  // estancado). Los estados de reconexión/desconexión son problema inmediato.
  const connectingStuck = isConnecting && connectingSecs >= CONNECTING_STUCK_DELAY_S;
  // Con `nivelAviso` presente, quien decide cuándo interrumpir es el detector de avisos —pero
  // SOLO una vez que la llamada llegó a establecerse. Antes de eso no hay nada que preservar y
  // el detector ni siquiera existe: si el primer intento termina en `Disconnected`, callar
  // dejaría al médico mirando una pantalla muerta sin explicación.
  const escalaAvisos = nivelAviso !== undefined && yaConectoRef.current;
  const inTrouble = (isReconnecting || isDisconnected)
    && (!escalaAvisos || nivelAviso === 'grave');

  useEffect(() => {
    if (current === ConnectionState.Connected) yaConectoRef.current = true;
  }, [current]);

  // Cronómetro del `Connecting` inicial: cuenta mientras siga en ese estado y
  // se reinicia al salir de él.
  useEffect(() => {
    if (!isConnecting) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConnectingSecs(0);
      return;
    }
    const t = setInterval(() => setConnectingSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [isConnecting]);

  useEffect(() => {
    if (inTrouble || connectingStuck) {
      // Problema activo → visible de inmediato, cancela cualquier ocultado.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTroubled(true);
      return;
    }
    if (isConnected) {
      // Conexión restablecida → ocultar tras un breve margen estable.
      const t = setTimeout(() => setTroubled(false), HIDE_DEBOUNCE_MS);
      return () => clearTimeout(t);
    }
  }, [inTrouble, connectingStuck, isConnected]);

  useEffect(() => {
    if (!troubled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setElapsed(0);
      return;
    }
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [troubled]);

  if (!troubled) return null;

  // Botón FIJO: tras unos segundos de intermitencia mostramos el botón y lo
  // mantenemos visible (no depende del estado instantáneo connected/reconnecting/
  // disconnected) hasta que `troubled` se apague al restablecerse la conexión.
  // En un `Connecting` estancado el botón aparece de inmediato (ya esperamos el
  // umbral CONNECTING_STUCK_DELAY_S para llegar hasta aquí).
  const showRetry = !!onRetry && (connectingStuck || elapsed >= RETRY_BUTTON_DELAY_S);

  return (
    <div
      className={styles.overlay}
      role="alert"
      aria-live="assertive"
    >
      <div className={styles.card}>
        <div
          className={`${styles.iconWrap} ${isDisconnected ? styles.iconDisconnected : ''}`}
          aria-hidden="true"
        >
          {isDisconnected ? <WifiOff size={28} /> : <Loader2 size={28} className={styles.spin} />}
        </div>
        <h3 className={styles.title}>
          {isDisconnected ? 'Conexión perdida' : connectingStuck ? 'Conectando…' : 'Reconectando…'}
        </h3>
        <p className={styles.text}>
          {/* Si el backend ya sabe POR QUÉ no conecta (el servidor no respondió
              al preparar la sala, o estamos fuera del horario de servicio), su
              explicación gana sobre el texto genérico: "está tardando más de lo
              normal" no le sirve de nada a quien lleva un minuto esperando. */}
          {motivo
            ? motivo
            : escalaAvisos && nivelAviso === 'grave'
              ? 'No logramos restablecer el audio o el video. Si no puede escuchar o ver al paciente, '
                + 'o la imagen está congelada, use el botón para volver a conectarse.'
            : isDisconnected
              ? 'No fue posible reconectar con la sala. Verifique su conexión a internet e intente nuevamente.'
              : connectingStuck
                ? 'La conexión con la sala está tardando más de lo normal. Puede reintentar ahora o esperar unos segundos.'
                : 'Estamos restableciendo su conexión con la sala. No cierre esta ventana.'}
        </p>
        {showElapsed && (
          <span className={styles.elapsed}>
            {connectingStuck
              ? <>Conectando hace <strong>{connectingSecs}s</strong>…</>
              : <>Conexión inestable hace <strong>{elapsed}s</strong>…</>}
          </span>
        )}
        {showRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              marginTop: 16,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              background: '#1A8048',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} aria-hidden="true" />
            Reintentar conexión
          </button>
        )}
      </div>
    </div>
  );
}
